// v4 addendum "Make it in Suno" (docs/product/spec-v4-suno.md, ticket V4-M1): the web's copy of the pack and its state
// (the web doesn't import core; keep in step with product/core/src/suno.ts and product/api/src/suno.ts sunoView), the
// parser for the API's `suno`, and the pure bits SunoPack.tsx shows: the steps, the fields to copy, the sliders with
// their "why", the state labels and the licensing note.
import type { NoticeData } from "./nextstep";
import { scriptCredits } from "./prices";

export type SunoModel = "v6" | "v6-wild" | "v6-mini";

export type SunoPackData = {
  model: SunoModel;
  instrumental: boolean;
  title: string;
  style: string;
  exclude: string;
  lyrics: string;
  vocalGender?: "male" | "female";
  weirdness: number;
  styleInfluence: number;
  variety: number;
  maxMode: boolean;
  targetLengthS: number;
  notes: string[];
  limits: { styleChars: number; lyricsChars: number; excludeChars: number; titleChars: number; maxLengthS: number; checked: string };
};

export type SunoWhy = Record<"model" | "weirdness" | "styleInfluence" | "variety" | "maxMode", string>;

export type SunoState = {
  pack: SunoPackData | null;
  version: number;
  status: "waiting" | "received";
  requestedAt: string;
  edited: boolean;
  videoS: number;
  assetId: string | null;
  songName: string | null;
  songS: number | null;
  receivedAt: string | null;
  fit: "loop" | "shorten" | null;
  needsFit: boolean;
  notices: NoticeData[];
  why: SunoWhy;
  /** what writing a (new) pack costs */
  credits: number;
};

/** What PATCH takes: a field, a slider, or the loop-or-shorten choice. */
export type SunoPatch = Partial<Omit<SunoPackData, "limits" | "vocalGender">> & { vocalGender?: "male" | "female" | null; fit?: "loop" | "shorten" };
/** What POST takes (the Director's suno_pack args). */
/** expectCredits (PRICE-FIRST): the price on the button; a dearer pack is refused (400 CONFIRM_REQUIRED) */
export type SunoWriteArgs = { instrumental?: boolean; mood?: string; notes?: string; freePlan?: boolean; expectCredits?: number };

/** Where the user makes the song. */
export const SUNO_URL = "https://suno.com/create";
/** The price of a pack when the API hasn't said: the script price (5 credits, 10 from 2027-01-01). */
export const sunoPackCredits = (now?: Date): number => scriptCredits(now);

// ------------------------------------------------------------------ parsing

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const str = (v: unknown, d = "") => (typeof v === "string" ? v : d);
const num = (v: unknown, d = 0) => (typeof v === "number" && Number.isFinite(v) ? v : d);
const MODELS: SunoModel[] = ["v6", "v6-wild", "v6-mini"];
const NO_WHY: SunoWhy = { model: "", weirdness: "", styleInfluence: "", variety: "", maxMode: "" };

export function normSunoPack(v: unknown): SunoPackData | null {
  if (!isObj(v) || typeof v.style !== "string") return null;
  const l = isObj(v.limits) ? v.limits : {};
  return {
    model: MODELS.includes(v.model as SunoModel) ? (v.model as SunoModel) : "v6",
    instrumental: v.instrumental !== false,
    title: str(v.title),
    style: str(v.style),
    exclude: str(v.exclude),
    lyrics: str(v.lyrics),
    ...(v.vocalGender === "male" || v.vocalGender === "female" ? { vocalGender: v.vocalGender } : {}),
    weirdness: num(v.weirdness, 35),
    styleInfluence: num(v.styleInfluence, 75),
    variety: num(v.variety, 0),
    maxMode: v.maxMode === true,
    targetLengthS: num(v.targetLengthS, 60),
    notes: Array.isArray(v.notes) ? v.notes.filter((x): x is string => typeof x === "string") : [],
    limits: {
      styleChars: num(l.styleChars, 1000),
      lyricsChars: num(l.lyricsChars, 3000),
      excludeChars: num(l.excludeChars, 300),
      titleChars: num(l.titleChars, 80),
      maxLengthS: num(l.maxLengthS, 480),
      checked: str(l.checked),
    },
  };
}

/** The API's `suno` (or `{suno}`); null when the video has none. */
export function normSuno(raw: unknown): SunoState | null {
  const o = isObj(raw) && isObj(raw.suno) ? raw.suno : raw;
  if (!isObj(o) || (o.status !== "waiting" && o.status !== "received")) return null;
  const w = isObj(o.why) ? o.why : {};
  return {
    pack: normSunoPack(o.pack),
    version: num(o.version),
    status: o.status,
    requestedAt: str(o.requestedAt),
    edited: o.edited === true,
    videoS: num(o.videoS),
    assetId: str(o.assetId) || null,
    songName: str(o.songName) || null,
    songS: typeof o.songS === "number" ? o.songS : null,
    receivedAt: str(o.receivedAt) || null,
    fit: o.fit === "loop" || o.fit === "shorten" ? o.fit : null,
    needsFit: o.needsFit === true,
    notices: (Array.isArray(o.notices) ? o.notices : [])
      .filter(isObj)
      .filter((n) => str(n.text))
      .map((n) => ({ tone: n.tone === "error" || n.tone === "warning" ? n.tone : "info", text: str(n.text), ...(str(n.code) ? { code: str(n.code) } : {}) })),
    why: { ...NO_WHY, ...Object.fromEntries(Object.entries(w).filter(([, x]) => typeof x === "string")) } as SunoWhy,
    credits: num(o.credits, sunoPackCredits()),
  };
}

// ------------------------------------------------------------------ what the fold shows

export const time = (s: number) => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.round(Math.max(0, s) % 60)).padStart(2, "0")}`;

/** The five steps, in the user's words. */
export const SUNO_STEPS = [
  "Open suno.com/create and switch to Custom.",
  "Copy each field below into Suno.",
  "Set the sliders the same way.",
  "Create, pick your favourite, and download it (MP3 or WAV).",
  "Upload it here.",
] as const;

/** Shown above the upload (the spec's licensing note; verify against Suno's current terms before launch). */
export const SUNO_LICENSE_NOTE =
  "Songs made on Suno's free plan are for non-commercial use. To use a song in a video you publish or sell, make it on a paid Suno plan.";

export type SunoFieldKey = "title" | "style" | "exclude" | "lyrics";
export type SunoField = { key: SunoFieldKey; label: string; text: string; hint: string; limit?: number; multiline: boolean };

/** The boxes to copy, in Suno's order. An instrumental has no lyrics box (Instrumental is a switch, in the sliders). */
export function sunoFields(p: SunoPackData): SunoField[] {
  const out: SunoField[] = [];
  if (!p.instrumental) out.push({ key: "lyrics", label: "Lyrics", text: p.lyrics, hint: "Paste into Lyrics. The [Verse] and [Chorus] tags tell Suno the song's shape.", limit: p.limits.lyricsChars, multiline: true });
  out.push({ key: "style", label: "Style of Music", text: p.style, hint: "Paste it whole. v6 follows it word for word.", limit: p.limits.styleChars, multiline: true });
  out.push({ key: "exclude", label: "Exclude styles", text: p.exclude, hint: "Under Advanced options.", limit: p.limits.excludeChars, multiline: false });
  out.push({ key: "title", label: "Song title", text: p.title, hint: "Optional, but it helps you find the song later.", limit: p.limits.titleChars, multiline: false });
  return out;
}

/** Exactly what a field's Copy button puts on the clipboard. */
export function copyText(p: SunoPackData, key: SunoFieldKey): string {
  return (key === "lyrics" ? (p.instrumental ? "" : p.lyrics) : p[key]).trim();
}

export type SunoSetting = { key: string; label: string; value: string; why: string };

/** The settings to match by hand, read-only, each with a short "why". */
export function sunoSettings(p: SunoPackData, why: SunoWhy): SunoSetting[] {
  return [
    { key: "model", label: "Model", value: p.model, why: why.model },
    { key: "instrumental", label: "Instrumental", value: p.instrumental ? "On" : "Off", why: p.instrumental ? "A bed under the voice: no singing." : "This one has words." },
    ...(!p.instrumental ? [{ key: "vocalGender", label: "Vocal gender", value: p.vocalGender === "male" ? "Male" : p.vocalGender === "female" ? "Female" : "Leave unset", why: "Under Advanced options." }] : []),
    { key: "weirdness", label: "Weirdness", value: `${p.weirdness} %`, why: why.weirdness },
    { key: "styleInfluence", label: "Style Influence", value: `${p.styleInfluence} %`, why: why.styleInfluence },
    { key: "variety", label: "Variety", value: p.variety === 0 ? "Off" : `${p.variety} %`, why: why.variety },
    { key: "maxMode", label: "Max Mode", value: p.maxMode ? "On" : "Off", why: why.maxMode },
    { key: "length", label: "Length", value: `At least ${time(p.targetLengthS)}`, why: "Suno can't be told an exact length, so pick a take at least this long." },
  ];
}

/** The fold's one-line state (its summary row) and the pill tone. */
export function sunoStateLabel(s: SunoState | null): { text: string; tone: "off" | "busy" | "done" | "draft" } {
  if (!s) return { text: "Not started", tone: "off" };
  if (s.status === "waiting") return s.pack ? { text: "Waiting for your Suno song", tone: "busy" } : { text: "Write the prompts first", tone: "off" };
  if (s.needsFit) return { text: "Song in: loop it or shorten the video?", tone: "draft" };
  return { text: `Your song is in${s.songS ? ` · ${time(s.songS)}` : ""}`, tone: "done" };
}

/** The upload button's label. */
export function uploadLabel(s: SunoState | null): string {
  return s?.status === "received" ? "Upload a different song" : "Upload your Suno song";
}

/** A file the picker may take: MP3, WAV, M4A/AAC, at most 100 MB (the upload pipeline checks again). */
export const SONG_ACCEPT = "audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a,audio/aac,.mp3,.wav,.m4a,.aac";
export const SONG_MAX_BYTES = 100 * 1024 * 1024;
export function songProblem(f: { name: string; size: number; type: string }): string | null {
  if (f.size > SONG_MAX_BYTES) return "That song is over 100 MB.";
  const ok = /^audio\/(mpeg|mp3|wav|x-wav|wave|mp4|x-m4a|aac)$/.test(f.type) || /\.(mp3|wav|m4a|aac)$/i.test(f.name);
  return ok ? null : "Download it from Suno as an MP3 or WAV.";
}

/** "Uploading 42 %" while it goes; null when idle. */
export function progressText(sent: number | null): string | null {
  if (sent === null) return null;
  return sent >= 1 ? "Finishing up" : `Uploading ${Math.round(sent * 100)} %`;
}
